<!-- 講座画面、メンバー一覧選択時のページ -->
<script setup lang="ts">
  import { tableHeaders, spRows } from "~/components/Atoms/DataTable/PlanOfferListHeader";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { DEFAULT_PAGE_SIZE } from "~/consts/contentsConfig";
  import { useCampaignStore } from "~/stores/CampaignStore";
  import { Campaign } from "~/entities/Campaign";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  interface Props {
    planInfo: PlanInfo;
  }
  const localProps = withDefaults(defineProps<Props>(), {});

  defineEmits(["onBack"]);

  const campaignStore = useCampaignStore();
  const snackBarStore = useSnackBarStore();

  const page = ref(1);
  const pageSize = ref(DEFAULT_PAGE_SIZE);
  const showDetail = ref(false);

  const sort = ref<"desc" | "asc">("desc");
  const search = ref("");

  // レスポンシブ対応
  const isTablet = isTabletAndSpDisplayRef();

  const planId = computed<string>(() => {
    return (useRoute().query.planId as string) || localProps.planInfo.id;
  });

  // トランジションエフェクトの関係でデータ取得は外部から行う
  const fetch = () => {
    const options = {
      sort: sort.value,
      page_size: pageSize.value,
      page: page.value,
      plan_id: planId.value,
    };
    campaignStore.getCampaigns(options);
    // ページが変わったらスクロール位置をトップに戻す
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  const loading = computed<boolean>(() => campaignStore.loading);

  const computedPage = computed<number>({
    get: () => page.value,
    set: value => {
      page.value = value;
      fetch();
    },
  });

  const showPagination = computed<boolean>(() => {
    if (campaignStore.campaignResponse?.totalPages) {
      return campaignStore.campaignResponse.totalPages > 1;
    }
    return false;
  });

  watch(
    () => search.value,
    () => {
      page.value = 1;
      fetch();
    },
  );

  const handleCreate = () => {
    if (planId.value) {
      historyStore.to(`/plan/${localProps.planInfo.id}/distribute/offer/create?planId=${planId.value}`);
    } else {
      historyStore.to(`/plan/${localProps.planInfo.id}/distribute/offer/create`);
    }
  };

  const handleAction = (action: { text: string; value: string }, campaign: Campaign) => {
    if (action.value === "edit-item") {
      campaignStore.selectedCampaign = campaign;
      historyStore.to(`/plan/${localProps.planInfo.id}/distribute/offer/edit`);
    } else if (action.value === "edit-history") {
      historyStore.to(`/plan/${localProps.planInfo.id}/distribute/offer/${campaign.campaignId}/history`);
    } else if (action.value === "delete-item") {
      onDelete(campaign);
    }
  };

  const onDelete = async (campaign: Campaign) => {
    try {
      await campaignStore.deleteCampaign(String(campaign.campaignId));
      snackBarStore.set(`${campaign.campaignTitle}を削除しました`);
      await fetch();
    } catch (error: any) {
      snackBarStore.setError("キャンペインの削除に失敗しました");
      console.error(error);
    }
  };

  const getTableHeaders = () => {
    return tableHeaders(handleAction);
  };

  onMounted(() => {
    fetch();
  });

  defineExpose({
    fetch,
    loading,
  });
</script>
<template>
  <MoleculesContainerMain
    v-model:search="search"
    :show-search="false"
    :show-plans="true"
    :enable-all-plan="false"
    title="ワンタイムオファー"
    :default-plan-id="planInfo.id"
    :loading="loading"
    svg-icon="assets/images/icon/member-offer.webp"
    @on-back="$emit('onBack')"
  >
    <template #header-btns>
      <v-btn class="add-campaign" @click="handleCreate">
        <v-icon icon="$mdiPlus" color="white" size="large" />
        <span v-if="isTablet" class="text-white">作成 </span>
        <span v-if="!isTablet" class="text-white"> ワンタイムオファーを作成 </span>
      </v-btn>
    </template>
    <div class="mx-2 mt-2">
      <div v-if="!loading && isTablet" class="d-flex flex-wrap justify-end">
        <v-btn variant="tonal" @click="showDetail = !showDetail">
          <v-icon :icon="showDetail ? '$mdiChevronUp' : '$mdiChevronDown'" />
        </v-btn>
      </div>

      <template v-if="loading">
        <AtomsDataTablePlanOfferCardLoading v-for="index in DEFAULT_PAGE_SIZE" :key="index" />
      </template>
      <template v-else>
        <MoleculesDatatable
          :show-detail="showDetail"
          :headers="getTableHeaders()"
          :sp-rows="spRows()"
          :items="campaignStore.campaignResponse?.campaigns || []"
        />
      </template>

      <v-sheet color="transparent" class="pb-4">
        <AtomsPagination
          v-if="showPagination"
          v-model:page="computedPage"
          :total-pages="campaignStore.campaignResponse?.totalPages || 0"
        />
      </v-sheet>
    </div>
  </MoleculesContainerMain>
</template>

<style lang="scss" scoped>
  .add-campaign {
    background-color: rgba(50, 50, 50, 1);
    font-weight: 700;
  }
</style>
